import { ThunkApiType } from 'types/storeTypes';
import authFetch, { checkForUnauthorizedResponse } from '../../utils/axios';

import { thunkLoadingParams } from 'feature/progressIndicator/progressIndicatorSlice';
import { Eye, IClaim, IPrescription, REASON_CODE_TYPOLOGY } from 'types/Claim';
import { prescriptionColumnsConfiguration } from 'utils/prescription.hook';

export const getClaimDetailsThunk = async (
	arg: thunkLoadingParams & { claimUuid: string },
	thunkAPI: ThunkApiType,
) => {
	let getClaimDetailsURL = `/claim/${arg.claimUuid}`;

	try {
		const response = await authFetch.get(getClaimDetailsURL);
		return response.data;
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};

export const getPrescriptionsThunk = async (
	_: object,
	thunkAPI: ThunkApiType,
) => {
	const {
		member: { subscriptionUuid },
	} = thunkAPI.getState().member;
	try {
		const response = await authFetch.get(
			`/prescription?subscriptionUuid=${subscriptionUuid}`,
		);
		return response.data;
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};

export const saveClaimThunk = async (
	input: { switchPage: boolean; redirect: boolean },
	thunkAPI: ThunkApiType,
) => {
	let formData = thunkAPI.getState().claim.claimData;
	if (input.switchPage) {
		formData = { ...formData, step: formData.step ? formData.step + 1 : 1 };
	}

	formData = {
		...formData,
		newPrescriptionContent: formData.newPrescriptionContent
			? fixPrescription(formData.newPrescriptionContent, 'BE')
			: undefined,
		oldPrescriptionContent: formData.oldPrescriptionContent
			? fixPrescription(formData.oldPrescriptionContent, 'BE')
			: undefined,
	};

	try {
		const response =
			formData.status.id === 18
				? await authFetch.post('/claim/pending', formData)
				: await authFetch.post('/claim', formData);
		return response.data;
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};

export const calculateEstimatesThunk = async (
	formData: IClaim,
	thunkAPI: ThunkApiType,
) => {
	formData = {
		...formData,
		newPrescriptionContent: formData.newPrescriptionContent
			? fixPrescription(formData.newPrescriptionContent, 'BE')
			: undefined,
		oldPrescriptionContent: formData.oldPrescriptionContent
			? fixPrescription(formData.oldPrescriptionContent, 'BE')
			: undefined,
	};

	try {
		const response = await authFetch.post('/claim/calculateCost', formData);
		return response.data;
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};

export const submitClaimThunk = async (
	formData: IClaim,
	thunkAPI: ThunkApiType,
) => {
	formData = {
		...formData,
		newPrescriptionContent: formData.newPrescriptionContent
			? fixPrescription(formData.newPrescriptionContent, 'BE')
			: undefined,
		oldPrescriptionContent: formData.oldPrescriptionContent
			? fixPrescription(formData.oldPrescriptionContent, 'BE')
			: undefined,
	};

	try {
		const response = await authFetch.post('/claim/submit', formData);
		return response.data;
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};

export const getDoctorTypesThunk = async (
	_: string,
	thunkAPI: ThunkApiType,
) => {
	try {
		const response = await authFetch.get('/doctor/typologies');
		return response.data;
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};

export const fixPrescription = (
	prescription: IPrescription,
	destination: 'FE' | 'BE',
) => {
	const columns = prescriptionColumnsConfiguration({
		prescription,
		checkEmptyFields: false,
		isOldPrescription: false,
		isSingleVision: true,
		isContactOnly: false,
	});

	const formatterToPick =
		destination === 'FE' ? 'formatValueVideo' : 'formatValueBackend';

	const newPrescription = {
		...prescription,
		os: { ...prescription.os },
		od: { ...prescription.od },
	};

	(['od', 'os'] as Eye[]).forEach(eye =>
		columns.forEach(col => {
			newPrescription[eye][col.property] = (
				col[formatterToPick]
					? col[formatterToPick]!(prescription[eye][col.property] as string)
					: prescription[eye][col.property]
			) as string;
		}),
	);
	return newPrescription;
};

export const deleteClaimThunk = async (
	{ reasonCode, statusCode }: { reasonCode: string; statusCode: string },
	thunkAPI: ThunkApiType,
) => {
	try {
		const claimUUID = thunkAPI.getState().hooks.deleteClaim.claimUUID;

		const response = await authFetch.put(`/claim/${claimUUID}/status`, {
			reasonCode,
			statusCode,
		});
		return response.data;
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};

export const getReasonCodeVoidThunk = async (
	_: object,
	thunkAPI: ThunkApiType,
) => {
	try {
		const response = await authFetch.get(
			`/reasonCode?typology=${REASON_CODE_TYPOLOGY.VOID}`,
		);
		return response.data;
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};

export const getReasonCodeCanceledThunk = async (
	_: object,
	thunkAPI: ThunkApiType,
) => {
	try {
		const response = await authFetch.get(
			`/reasonCode?typology=${REASON_CODE_TYPOLOGY.CANCEL}`,
		);
		return response.data;
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};

export const getReasonCodePendingThunk = async (
	_: object,
	thunkAPI: ThunkApiType,
) => {
	try {
		const response = await authFetch.get(
			`/reasonCode?typology=${REASON_CODE_TYPOLOGY.PENDING}`,
		);
		return response.data;
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};

export const getReasonCodeRejectThunk = async (
	_: object,
	thunkAPI: ThunkApiType,
) => {
	try {
		const response = await authFetch.get(
			`/reasonCode?typology=${REASON_CODE_TYPOLOGY.REJECT}`,
		);
		return response.data;
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};

export const getReasonCodeReturnThunk = async (
	_: object,
	thunkAPI: ThunkApiType,
) => {
	try {
		const response = await authFetch.get(
			`/reasonCode?typology=${REASON_CODE_TYPOLOGY.RETURN}`,
		);
		return response.data;
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};

export const returnClaimThunk = async (
	{ reasonCode }: { reasonCode: string },
	thunkAPI: ThunkApiType,
) => {
	try {
		const claimUUID = thunkAPI.getState().claim.claimData.claimUUID;
		const attachments = thunkAPI.getState().claim.claimData.attachments.return;

		const response = await authFetch.post(`/returns/claims/${claimUUID}`, {
			reasonCode,
			attachments: {
				returnDetail: attachments,
			},
		});
		return response.data;
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};
